/**
 * Client-local 2D platformer — a TypeScript port of the prototype's
 * `usePlatformer` hook (`docs/design/flow/ui.jsx:322`). Run/jump with gravity,
 * grounded against a flat floor (+ optional one-way platforms), clamped to
 * `[minX, maxX]`.
 *
 * This is **render/position only** — it is driven by the Lobby scene's local
 * input and NEVER goes on the wire (the plaza avatar is purely cosmetic /
 * client-local per `docs/design/contracts/slice2-protocol.md` §3). It owns no
 * server authority; it just answers "where is MY Pon on the plaza floor".
 *
 * The host scene advances it once per frame with `step(deltaMs)` (the prototype
 * runs a ~60fps `setTimeout`; in Phaser we use the scene `update(time, delta)`
 * so it pauses with the scene), reads `pos`/`facing`/`ponState` to render, and
 * mutates `input` (left/right) + calls `jump()` (the grounded-gated edge).
 */

export interface PlatformerPlatform {
  x: number;
  y: number;
  w: number;
  /**
   * `false`/undefined = one-way (land on top, pass up through from below — floating
   * ledges). `true` = SOLID box: also blocks the sides and the underside (you can't
   * jump up through it), with the box reaching `h` px below its top surface.
   */
  solid?: boolean;
  /** Solid-box height below the top surface `y` (only used when `solid`). */
  h?: number;
}

export interface PlatformerConfig {
  groundY: number;
  spawn: { x: number; y: number };
  minX: number;
  maxX: number;
  platforms?: PlatformerPlatform[];
  gravity?: number;
  jumpVel?: number;
  speed?: number;
  /** Pon body half-width / height for solid-box AABB collision (defaults 16 / 46). */
  bodyHalf?: number;
  bodyH?: number;
}

export type PonAnimState = 'idle' | 'walk' | 'jump' | 'fall';

export class Platformer {
  /** Held-direction input (mutated by the host from keyboard + touch). */
  readonly input = { left: false, right: false };
  /** Horizontal speed multiplier (host-set, e.g. <1 while carrying an absorbed box). */
  speedScale = 1;

  private readonly cfg: Required<PlatformerConfig>;
  private px: number;
  private py: number;
  private vx = 0;
  private vy = 0;
  private grounded = true;
  private moving = false;
  private facingDir: 'left' | 'right' = 'right';

  constructor(cfg: PlatformerConfig) {
    this.cfg = {
      platforms: [],
      gravity: 0.9,
      jumpVel: 14.5,
      speed: 2.8,
      bodyHalf: 16,
      bodyH: 46,
      ...cfg,
    };
    this.px = cfg.spawn.x;
    this.py = cfg.spawn.y;
  }

  /** Grounded-gated jump edge (prototype `doJump`). No-op while airborne. */
  jump(): void {
    if (this.grounded) {
      this.vy = -this.cfg.jumpVel;
      this.grounded = false;
    }
  }

  /**
   * Advance one frame. `deltaMs` is the real frame time; like the prototype we
   * normalise to 60fps steps (`dt = deltaMs/16.67`, capped at 2.2 so a tab
   * stall can't fling the Pon across the plaza).
   */
  step(deltaMs: number): void {
    const dt = Math.min(2.2, deltaMs / 16.67);
    const { groundY, minX, maxX, platforms, gravity, speed, bodyHalf, bodyH } = this.cfg;

    const ax = (this.input.right ? 1 : 0) - (this.input.left ? 1 : 0);
    this.vx = ax * speed * this.speedScale;

    // --- horizontal move, then push out of any SOLID platform's sides ---
    const prevPx = this.px;
    let nx = Math.max(minX, Math.min(maxX, this.px + this.vx * dt));
    for (const pl of platforms) {
      if (!pl.solid) continue;
      const left = pl.x - pl.w / 2;
      const right = pl.x + pl.w / 2;
      const bottom = pl.y + (pl.h ?? 1e4);
      // Only a side hit if the body straddles the box vertically (feet BELOW the
      // top surface — so standing ON top never counts as a side collision).
      const vOverlap = this.py > pl.y + 0.5 && this.py - bodyH < bottom;
      if (vOverlap && nx + bodyHalf > left && nx - bodyHalf < right) {
        nx = prevPx <= pl.x ? left - bodyHalf : right + bodyHalf;
      }
    }
    this.px = Math.max(minX, Math.min(maxX, nx));

    // --- vertical move, then floor / land-on-top / bonk-underside ---
    const prevFeet = this.py;
    this.vy += gravity * dt;
    if (this.vy > 22) this.vy = 22;
    this.py = this.py + this.vy * dt;

    let onGround = false;
    if (this.py >= groundY) {
      this.py = groundY;
      this.vy = 0;
      onGround = true;
    }
    for (const pl of platforms) {
      const horiz = this.px + bodyHalf > pl.x - pl.w / 2 && this.px - bodyHalf < pl.x + pl.w / 2;
      if (!horiz) continue;
      // land on the top surface when falling (one-way + solid alike)
      if (this.vy >= 0 && prevFeet <= pl.y + 8 && this.py >= pl.y) {
        this.py = pl.y;
        this.vy = 0;
        onGround = true;
      }
      // SOLID: bonk the underside when rising up into it
      if (pl.solid) {
        const bottom = pl.y + (pl.h ?? 1e4);
        const prevHead = prevFeet - bodyH;
        if (this.vy < 0 && prevHead >= bottom - 0.5 && this.py - bodyH <= bottom) {
          this.py = bottom + bodyH;
          this.vy = 0;
        }
      }
    }

    this.grounded = onGround;
    this.moving = ax !== 0;
    if (ax !== 0) this.facingDir = ax < 0 ? 'left' : 'right';
  }

  get x(): number {
    return this.px;
  }

  /** Force the horizontal position (clamped to bounds) — used by external collision
   *  resolution (e.g. the plaza's solid/pushable crates). */
  setX(x: number): void {
    this.px = Math.max(this.cfg.minX, Math.min(this.cfg.maxX, x));
  }

  /** True when descending (or level) — i.e. eligible to land on a surface. */
  isFalling(): boolean {
    return this.vy >= 0;
  }

  /** Land the feet on an external surface (e.g. the top of a crate): plant the
   *  feet, kill downward velocity, and mark grounded so the Pon can jump again. */
  landOn(surfaceY: number): void {
    this.py = surfaceY;
    this.vy = 0;
    this.grounded = true;
  }

  get y(): number {
    return this.py;
  }

  get facing(): 'left' | 'right' {
    return this.facingDir;
  }

  get ponState(): PonAnimState {
    if (!this.grounded) return this.vy < 0 ? 'jump' : 'fall';
    return this.moving ? 'walk' : 'idle';
  }
}
